/**
 * Pure H OS · Sprint 12 · API client.
 *
 * Single fetch-based client for /api/pureh/*. Uses HttpOnly cookies for
 * auth (credentials: "include"). On 401 the caller decides whether to
 * refresh + retry once or bounce to /pureh/login.
 */
declare const process: { env: Record<string, string | undefined> };

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/pureh`;

export class ApiError extends Error {
  status: number;
  constructor(status: number, msg: string) { super(msg); this.status = status; }
}

// ── Market scope (X-Market-Id header — same pattern as venture scope) ──
let ACTIVE_MARKET_ID: string | null = null;
export function setActiveMarketHeader(id: string | null): void { ACTIVE_MARKET_ID = id; }
export function getActiveMarketHeader(): string | null { return ACTIVE_MARKET_ID; }

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(ACTIVE_MARKET_ID ? { "X-Market-Id": ACTIVE_MARKET_ID } : {}),
      ...(init.headers || {}),
    },
    ...init,
  });
  if (res.ok) return res.status === 204 ? (undefined as T) : (await res.json()) as T;
  let detail = res.statusText;
  try { const j = await res.json(); detail = j.detail ?? detail; } catch { /* ignore */ }
  throw new ApiError(res.status, typeof detail === "string" ? detail : JSON.stringify(detail));
}

export type PurehUser = { id: string; email: string; name: string; role: "owner"|"admin"|"manager"|"employee"; isHoldingOwner?: boolean; groupOwner?: boolean };

export const api = {
  // Meta
  health: () => request<{ ok: boolean; db: string }>("/health"),

  // Auth
  login:   (email: string, password: string) =>
    request<PurehUser>("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  logout:  () => request<{ ok: true }>("/auth/logout", { method: "POST" }),
  refresh: () => request<{ ok: true }>("/auth/refresh", { method: "POST" }),
  me:      () => request<PurehUser>("/auth/me"),

  // Data
  bootstrap: () => request<Record<string, unknown[]>>("/data"),
  list:      (collection: string) => request<unknown[]>(`/data/${collection}`),
  patch:     (collection: string, id: string, patch: Record<string, unknown>, comment?: string) =>
    request<Record<string, unknown>>(`/data/${collection}/${id}`, {
      method: "PATCH", body: JSON.stringify({ patch, comment }),
    }),
  create:    (collection: string, record: Record<string, unknown>, comment?: string) =>
    request<Record<string, unknown>>(`/data/${collection}`, {
      method: "POST", body: JSON.stringify({ record, comment }),
    }),
  transition: (collection: string, id: string, status: string, comment?: string) =>
    request<Record<string, unknown>>(`/data/${collection}/${id}/transition`, {
      method: "POST", body: JSON.stringify({ status, comment }),
    }),

  // Audit
  audit: (collection?: string, recordId?: string, limit = 50) => {
    const p = new URLSearchParams();
    if (collection) p.set("collection", collection);
    if (recordId) p.set("record_id", recordId);
    p.set("limit", String(limit));
    return request<unknown[]>(`/audit?${p.toString()}`);
  },

  // Company Profile
  companyProfile:      () => request<Record<string, unknown>>("/company-profile"),
  patchCompanyProfile: (patch: Record<string, unknown>) =>
    request<Record<string, unknown>>("/company-profile", {
      method: "PATCH", body: JSON.stringify({ patch }),
    }),

  // Live P&L
  pnlLive: () => request<Record<string, unknown>>("/finance/pnl-live"),

  // Uploads (base64) — Pre-Production Gap Closure + Enterprise Doc Engine
  upload: (name: string, mime: string, dataBase64: string, linkedTo?: string, replaces?: string) =>
    request<{ id: string; name: string; mime: string; sizeBytes: number; uploadedAt: string; linkedTo?: string; version?: number; rootId?: string; superseded?: boolean }>(
      "/uploads",
      { method: "POST", body: JSON.stringify({ name, mime, dataBase64, linkedTo, replaces }) },
    ),
  uploadPayload: (id: string) => request<{ id: string; name: string; mime: string; dataBase64: string }>(`/uploads/${id}`),
  uploads: () => request<unknown[]>("/uploads"),
  uploadVersions: (id: string) => request<unknown[]>(`/uploads/${id}/versions`),
  uploadDelete:   (id: string) => request<{ ok: boolean; deleted: string }>(`/uploads/${id}`, { method: "DELETE" }),

  // Admin
  listUsers:  () => request<PurehUser[]>("/admin/users"),
  createUser: (body: { email: string; password: string; name: string; role: PurehUser["role"] }) =>
    request<PurehUser>("/admin/users", { method: "POST", body: JSON.stringify(body) }),

  // Ventures (holding foundation)
  ventures:    () => request<Array<Record<string, unknown>>>("/ventures"),
  venture:     (id: string) => request<Record<string, unknown>>(`/ventures/${id}`),
  meAccess:    () => request<{ email: string; role: string; groupOwner: boolean; ventures: string[] }>("/me/access"),

  // Markets (system-wide scope dimension)
  markets:      () => request<Market[]>("/markets"),
  createMarket: (body: { name: string; code: string; currency: string; status?: string }) =>
    request<Market>("/markets", { method: "POST", body: JSON.stringify(body) }),
  patchMarket:  (id: string, patch: Record<string, unknown>) =>
    request<Market>(`/markets/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
};

export type Market = { id: string; name: string; code: string; currency: string; status: string };

// ==============================================================
// Corporate Identity (Workstream 01 — Company Setup)
// ==============================================================

export type CISection =
  | "registration" | "tax" | "banking" | "addresses"
  | "contacts" | "licenses" | "government" | "digital";

export type CIExtractKind = "cr" | "tax" | "license";

export type CIHealth = {
  ventureId: string;
  computedAt: string;
  healthScore: number;
  breakdown: {
    identityCompleteness: number;
    documentCurrency:     number;
    expiryHealth:         number;
    portalPresence:       number;
  };
  expired:            Array<{ id?: string; kind?: string; expiryDate?: string; daysAgo?: number }>;
  upcoming30d:        Array<{ id?: string; kind?: string; expiryDate?: string; daysAway?: number }>;
  missing:            string[];
  recommendedActions: string[];
};

export type CISnapshot = {
  ventureId:     string;
  profile:       Record<string, unknown>;
  registrations: Array<Record<string, unknown>>;
  taxes:         Array<Record<string, unknown>>;
  banking:       Array<Record<string, unknown>>;
  addresses:     Array<Record<string, unknown>>;
  contacts:      Array<Record<string, unknown>>;
  licenses:      Array<Record<string, unknown>>;
  government:    Array<Record<string, unknown>>;
  digital:       Array<Record<string, unknown>>;
  timeline:      Array<Record<string, unknown>>;
  health:        Partial<CIHealth>;
};

export const corporateIdentity = {
  snapshot: (ventureId: string) =>
    request<CISnapshot>(`/corporate-identity/${ventureId}`),

  patchProfile: (ventureId: string, patch: Record<string, unknown>) =>
    request<CISnapshot>(`/corporate-identity/${ventureId}/profile`, {
      method: "PATCH", body: JSON.stringify(patch),
    }),

  createRow: (ventureId: string, section: CISection, row: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/corporate-identity/${ventureId}/${section}`, {
      method: "POST", body: JSON.stringify(row),
    }),

  patchRow: (ventureId: string, section: CISection, rowId: string, patch: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/corporate-identity/${ventureId}/${section}/${rowId}`, {
      method: "PATCH", body: JSON.stringify(patch),
    }),

  deleteRow: (ventureId: string, section: CISection, rowId: string) =>
    request<{ ok: boolean; id: string }>(`/corporate-identity/${ventureId}/${section}/${rowId}`, {
      method: "DELETE",
    }),

  addTimelineEvent: (ventureId: string, event: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/corporate-identity/${ventureId}/timeline`, {
      method: "POST", body: JSON.stringify(event),
    }),

  // Multipart file upload → AI-extracted fields (preview only, no save)
  extract: async (ventureId: string, kind: CIExtractKind, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${BASE}/corporate-identity/${ventureId}/extract/${kind}`, {
      method: "POST",
      credentials: "include",
      body: fd,
    });
    if (!res.ok) {
      let detail = res.statusText;
      try { const j = await res.json(); detail = j.detail ?? detail; } catch { /* ignore */ }
      throw new ApiError(res.status, typeof detail === "string" ? detail : JSON.stringify(detail));
    }
    return res.json() as Promise<{ kind: string; extracted: Record<string, unknown>; filename: string }>;
  },

  getHealth: (ventureId: string) =>
    request<CIHealth>(`/corporate-identity/${ventureId}/health`),

  recomputeHealth: (ventureId: string) =>
    request<CIHealth>(`/corporate-identity/${ventureId}/health/recompute`, { method: "POST" }),

  // PDF blob downloads
  downloadReport: async (ventureId: string, kind: "company-profile" | "dd-package" | "corporate-info") => {
    const res = await fetch(`${BASE}/corporate-identity/${ventureId}/reports/${kind}.pdf`, {
      credentials: "include",
    });
    if (!res.ok) throw new ApiError(res.status, `Failed to download ${kind}.pdf`);
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${ventureId}-${kind}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

// ==============================================================
// Product Master (Workstream 02 — single source of truth)
// ==============================================================

export type PMEnumMap = {
  [k in "gender" | "season" | "concentration" | "collection" | "launchStatus" | "positioning" | "priceTier" | "lifecycle"]: {
    values: string[];
    labels: Record<string, string>;
  };
};

export type PMListItem = {
  id: string;
  ventureId: string;
  productCode: string;
  sku?: string | null;
  nameEn: string;
  nameAr?: string | null;
  family?: string | null;
  gender?: string | null;
  concentration?: string | null;
  collection?: string | null;
  season?: string | null;
  bottleSizeMl?: number | null;
  positioning?: string | null;
  lifecycle: string;
  launchStatus: string;
  status: string;
  listPriceEGP?: number | null;
  priceTier?: string | null;
  marginTargetPct?: number | null;
  updatedAt: string;
};

export type PMProduct = Record<string, any>;

export type PMMarketCoverage = {
  marketId: string;
  marketName: string;
  code: string;
  currency: string;
  available: boolean;
  price: number | null;
  launchStatus: string;
  stock: number | null;
  fulfillment: { method?: string | null; notes?: string };
  packagingCostPer100ml?: number | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
};

export type PMCogs = {
  productId: string;
  marketId: string | null;
  currency: string | null;
  bottleMl: number;
  components: { aromatics: number | null; alcohol: number | null; dpg: number | null; packaging: number | null };
  cogsPerBottle: number | null;
  complete: boolean;
  missing: string[];
  retailPrice: number | null;
  margin: number | null;
  marginPct: number | null;
  formula: { id: string; code: string; version: number } | null;
  assumptions: { concentratePct: number | null; dpgPct: number; densityGPerMl: number } | null;
};

export const productMaster = {
  enums: () => request<PMEnumMap>("/product-master/meta/enums"),

  markets: (ventureId: string, productId: string) =>
    request<{ productId: string; coverage: PMMarketCoverage[] }>(
      `/product-master/ventures/${ventureId}/products/${productId}/markets`,
    ),
  patchMarket: (ventureId: string, productId: string, marketId: string, patch: Record<string, unknown>) =>
    request<PMMarketCoverage>(
      `/product-master/ventures/${ventureId}/products/${productId}/markets/${marketId}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    ),
  cogs: (ventureId: string, productId: string, marketId: string, bottleMl = 100) =>
    request<PMCogs>(
      `/product-master/ventures/${ventureId}/products/${productId}/cogs?marketId=${marketId}&bottleMl=${bottleMl}`,
    ),

  list: (ventureId: string, filters: Record<string, string | number | undefined> = {}) => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== "" && v !== null) p.set(k, String(v)); });
    const qs = p.toString();
    return request<PMListItem[]>(`/product-master/ventures/${ventureId}/products${qs ? "?" + qs : ""}`);
  },

  get: (ventureId: string, productId: string) =>
    request<PMProduct>(`/product-master/ventures/${ventureId}/products/${productId}`),

  create: (ventureId: string, payload: Record<string, unknown>) =>
    request<PMProduct>(`/product-master/ventures/${ventureId}/products`, {
      method: "POST", body: JSON.stringify(payload),
    }),

  patch: (ventureId: string, productId: string, patch: Record<string, unknown>) =>
    request<PMProduct>(`/product-master/ventures/${ventureId}/products/${productId}`, {
      method: "PATCH", body: JSON.stringify(patch),
    }),

  softDelete: (ventureId: string, productId: string) =>
    request<{ ok: boolean; id: string; lifecycle: string }>(
      `/product-master/ventures/${ventureId}/products/${productId}`, { method: "DELETE" },
    ),

  consumers: (ventureId: string, productId: string) =>
    request<{ productMasterId: string; consumers: any[] }>(
      `/product-master/ventures/${ventureId}/products/${productId}/consumers`,
    ),

  // ── Product workspace add-ons ─────────────────────────────

  readiness: (ventureId: string, productId: string) =>
    request<PMReadiness>(`/product-master/ventures/${ventureId}/products/${productId}/readiness`),
  recomputeReadiness: (ventureId: string, productId: string) =>
    request<PMReadiness>(`/product-master/ventures/${ventureId}/products/${productId}/readiness/recompute`, { method: "POST" }),

  timeline: (ventureId: string, productId: string) =>
    request<PMEvent[]>(`/product-master/ventures/${ventureId}/products/${productId}/timeline`),
  appendTimeline: (ventureId: string, productId: string, body: { title: string; body?: string; meta?: any }) =>
    request<PMEvent>(`/product-master/ventures/${ventureId}/products/${productId}/timeline`, { method: "POST", body: JSON.stringify(body) }),

  listAssets: (ventureId: string, productId: string) =>
    request<PMAsset[]>(`/product-master/ventures/${ventureId}/products/${productId}/assets`),
  getAsset: (ventureId: string, productId: string, assetId: string) =>
    request<PMAsset & { dataBase64?: string | null; url?: string | null }>(`/product-master/ventures/${ventureId}/products/${productId}/assets/${assetId}`),
  createAsset: (ventureId: string, productId: string, body: {
    name: string; mime: string; role: string; dataBase64?: string; url?: string; caption?: string; altText?: string;
  }) => request<PMAsset>(`/product-master/ventures/${ventureId}/products/${productId}/assets`, { method: "POST", body: JSON.stringify(body) }),
  deleteAsset: (ventureId: string, productId: string, assetId: string) =>
    request<{ ok: boolean; id: string }>(`/product-master/ventures/${ventureId}/products/${productId}/assets/${assetId}`, { method: "DELETE" }),

  listDocuments: (ventureId: string, productId: string) =>
    request<PMDocument[]>(`/product-master/ventures/${ventureId}/products/${productId}/documents`),
  createDocument: (ventureId: string, productId: string, body: {
    name: string; mime: string; category: string; dataBase64?: string; url?: string; notes?: string;
  }) => request<PMDocument>(`/product-master/ventures/${ventureId}/products/${productId}/documents`, { method: "POST", body: JSON.stringify(body) }),
  deleteDocument: (ventureId: string, productId: string, docId: string) =>
    request<{ ok: boolean; id: string }>(`/product-master/ventures/${ventureId}/products/${productId}/documents/${docId}`, { method: "DELETE" }),

  revisions: (ventureId: string, productId: string) =>
    request<PMRevision[]>(`/product-master/ventures/${ventureId}/products/${productId}/revisions`),

  approvalMeta: () =>
    request<{ states: string[]; transitions: Record<string, string[]> }>("/product-master/meta/approval"),
  approvalTransition: (ventureId: string, productId: string, toState: string, comment?: string) =>
    request<PMApproval>(`/product-master/ventures/${ventureId}/products/${productId}/approval/transition`, {
      method: "POST",
      body: JSON.stringify({ toState, comment: comment || undefined }),
    }),

  // ── Export URLs (client resolves the file itself via fetch / window.open) ──
  exportUrl: (ventureId: string, productId: string,
                kind: "profile/pdf" | "profile/docx" | "profile/pptx"
                     | "sales/pdf" | "technical/pdf" | "marketing/pdf") =>
    `${process.env.REACT_APP_BACKEND_URL}/api/pureh/product-master/ventures/${ventureId}/products/${productId}/export/${kind}`,
};

export type PMReadiness = {
  score: number;
  breakdown: Record<string, number>;
  missing: string[];
  computedAt?: string;
};
export type PMEvent = {
  id: string; productId: string; ventureId: string; at: string;
  kind: string; title: string; body?: string;
  actorId?: string; actorEmail?: string; meta?: Record<string, any>;
};
export type PMAsset = {
  id: string; productId: string; ventureId: string;
  role: string; name: string; mime: string;
  caption?: string; altText?: string;
  url?: string | null; dataBase64?: string | null; sizeBytes?: number;
  uploadedAt: string; uploaderEmail?: string;
};
export type PMDocument = {
  id: string; productId: string; ventureId: string;
  category: string; name: string; mime: string;
  notes?: string; url?: string | null; sizeBytes?: number;
  uploadedAt: string; uploaderEmail?: string;
};
export type PMRevision = {
  id: string; at: string; actorEmail?: string; actorUserId?: string;
  operation: string;
  payload: { id?: string; fields?: string[]; diff?: Record<string, any>; version?: number; from?: string; to?: string; comment?: string };
};
export type PMApproval = {
  state: string;
  submittedBy?: string | null; submittedAt?: string | null;
  reviewedBy?: string | null;  reviewedAt?: string | null;
  approvedBy?: string | null;  approvedAt?: string | null;
  publishedBy?: string | null; publishedAt?: string | null;
  rejectedBy?: string | null;  rejectedAt?: string | null;
  rejectedReason?: string | null;
  history: Array<{ at: string; from: string; to: string; actorEmail?: string; comment?: string | null }>;
};

// ── Knowledge Import ─────────────────────────────────────────────

export type KIFile = {
  fileId: string; name: string; mime: string; kind: string; sizeBytes?: number;
  matchedCode?: string | null; matchStatus?: string;
  parsed?: boolean; parseError?: string | null;
  textPreview?: string; pageCount?: number; paragraphCount?: number;
  sheets?: string[]; recordCount?: number;
  uploadedAt?: string;
};

export type KIProposal = {
  value: any; confidence: number; aiGenerated: boolean;
  language?: string | null; sourceSnippet?: string | null;
  sourcePage?: any; sourceSheet?: string | null;
  sourceFileName?: string; sourceKind?: string;
  ownerEdited?: boolean; overwrite?: boolean; approved?: boolean;
};

export type KIBatch = {
  id: string; ventureId: string; status: string;
  createdAt: string; createdBy?: string; creatorEmail?: string;
  parsedAt?: string; extractedAt?: string; appliedAt?: string;
  files: KIFile[];
  proposals: Record<string, {
    productCode: string; productId: string;
    fields: Record<string, KIProposal>;
    currentValues?: Record<string, any>;
  }>;
  extractionErrors?: Array<{ fileId?: string; error: string }>;
  applyResults?: Array<{ code: string; written?: string[]; skipped?: any[]; error?: string }>;
};

export const knowledgeImport = {
  listBatches: (ventureId: string) =>
    request<KIBatch[]>(`/product-master/knowledge-import/ventures/${ventureId}/batches`),
  createBatch: (ventureId: string) =>
    request<KIBatch>(`/product-master/knowledge-import/ventures/${ventureId}/batches`, { method: "POST" }),
  getBatch: (ventureId: string, batchId: string) =>
    request<KIBatch>(`/product-master/knowledge-import/ventures/${ventureId}/batches/${batchId}`),
  deleteBatch: (ventureId: string, batchId: string) =>
    request<{ ok: boolean; id: string }>(`/product-master/knowledge-import/ventures/${ventureId}/batches/${batchId}`, { method: "DELETE" }),

  uploadFile: (ventureId: string, batchId: string, body: { name: string; mime: string; dataBase64: string }) =>
    request<KIFile>(`/product-master/knowledge-import/ventures/${ventureId}/batches/${batchId}/files`, {
      method: "POST", body: JSON.stringify(body),
    }),

  parse:   (ventureId: string, batchId: string) =>
    request<{ ok: boolean; files: KIFile[]; matchedProductCount: number }>(
      `/product-master/knowledge-import/ventures/${ventureId}/batches/${batchId}/parse`, { method: "POST" }),
  extract: (ventureId: string, batchId: string) =>
    request<KIBatch>(`/product-master/knowledge-import/ventures/${ventureId}/batches/${batchId}/extract`, { method: "POST" }),
  preview: (ventureId: string, batchId: string) =>
    request<KIBatch>(`/product-master/knowledge-import/ventures/${ventureId}/batches/${batchId}/preview`),

  editProposal: (ventureId: string, batchId: string, code: string, body: {
    field: string; value?: any; overwrite?: boolean; approved?: boolean;
  }) => request<KIProposal>(`/product-master/knowledge-import/ventures/${ventureId}/batches/${batchId}/proposal/${code}`, {
    method: "PATCH", body: JSON.stringify(body),
  }),

  editFileMatch: (ventureId: string, batchId: string, fileId: string, matchedCode: string | null) =>
    request<{ ok: boolean; fileId: string; matchedCode: string | null }>(
      `/product-master/knowledge-import/ventures/${ventureId}/batches/${batchId}/file/${fileId}`, {
        method: "PATCH", body: JSON.stringify({ matchedCode }),
      }),

  apply: (ventureId: string, batchId: string, selections: Record<string, string[]>) =>
    request<{ ok: boolean; batchId: string; results: Array<{ code: string; written: string[]; skipped: any[] }> }>(
      `/product-master/knowledge-import/ventures/${ventureId}/batches/${batchId}/apply`, {
        method: "POST", body: JSON.stringify({ selections }),
      }),
};

// ── Raw Materials Library (Workstream 03) ────────────────────────

export type RMEnumMap = {
  ifraStatus: { values: string[]; labels: Record<string, string> };
  category:   { values: string[]; labels: Record<string, string> };
  unit:       { values: string[]; labels: Record<string, string> };
};

export type RawMaterial = {
  id: string; ventureId: string; code: string; name: string;
  supplier?: string | null; supplierRef?: string | null; casNumber?: string | null;
  category: string; ifraStatus: string; ifraMaxPct?: number | null; allergen?: boolean | null;
  unit: string; costPerUnit?: number | null; currency: string;
  stockOnHand?: number | null; reorderPoint?: number | null; leadTimeDays?: number | null;
  coaDocId?: string | null; sdsDocId?: string | null; safetyNotes?: string | null;
  tags: string[]; status: string; createdAt: string; updatedAt: string;
  usage?: {
    formulaCount: number;
    productCount: number;
    products: Array<{ id: string; productCode?: string | null; nameEn?: string | null }>;
  };
};

export type RMUsage = {
  rawMaterialId: string;
  code: string;
  formulas: Array<{ id: string; code: string; version: number; status: string; productMasterId: string; updatedAt: string }>;
  products: Array<{ id: string; productCode?: string | null; nameEn?: string | null; launchStatus?: string | null }>;
};

export const rawMaterials = {
  enums: () => request<RMEnumMap>("/raw-materials/meta/enums"),
  list: (ventureId: string, filters: { q?: string; category?: string; includeArchived?: boolean } = {}) => {
    const p = new URLSearchParams();
    if (filters.q) p.set("q", filters.q);
    if (filters.category) p.set("category", filters.category);
    if (filters.includeArchived) p.set("includeArchived", "true");
    const qs = p.toString();
    return request<RawMaterial[]>(`/raw-materials/ventures/${ventureId}/materials${qs ? "?" + qs : ""}`);
  },
  get: (ventureId: string, rmId: string) =>
    request<RawMaterial>(`/raw-materials/ventures/${ventureId}/materials/${rmId}`),
  create: (ventureId: string, payload: Partial<RawMaterial> & { code: string; name: string }) =>
    request<RawMaterial>(`/raw-materials/ventures/${ventureId}/materials`, {
      method: "POST", body: JSON.stringify(payload),
    }),
  patch: (ventureId: string, rmId: string, patch: Partial<RawMaterial>) =>
    request<RawMaterial>(`/raw-materials/ventures/${ventureId}/materials/${rmId}`, {
      method: "PATCH", body: JSON.stringify(patch),
    }),
  archive: (ventureId: string, rmId: string) =>
    request<{ ok: boolean; id: string }>(`/raw-materials/ventures/${ventureId}/materials/${rmId}`, {
      method: "DELETE",
    }),
  usage: (ventureId: string, rmId: string) =>
    request<RMUsage>(`/raw-materials/ventures/${ventureId}/materials/${rmId}/usage`),
};

// ── R&D · Development Laboratory (Workstream 03) ────────────────

export type RDIngredient = {
  rawMaterialId: string; grams: number; pct?: number | null;
  note?: string | null; orderIndex?: number;
};
export type RDFormula = {
  id: string; ventureId: string; productMasterId: string; code: string;
  version: number; parentFormulaId?: string | null; label?: string | null;
  status: "draft" | "active" | "approved" | "superseded" | "rejected";
  concentrationTarget?: string | null;
  ingredients: RDIngredient[];
  totalGrams: number; costPerBatch?: number | null; costPerMl?: number | null;
  macerationDays?: number | null; ifraCompliant?: boolean | null;
  allergensDeclared?: boolean | null; notes?: string | null;
  approvedAt?: string | null; approvedBy?: string | null;
  supersededAt?: string | null; createdAt: string; updatedAt: string;
};
export type RDSummary = {
  productMasterId: string; totalFormulas: number;
  approved?: RDFormula | null; latest?: RDFormula | null;
  counts: Record<string, number>;
  phase2: { experiments: number; blindSamples: number; panels: number; aiRecommendations: number; note: string };
};

export const rd = {
  meta: () => request<{ formulaStates: string[]; formulaTransitions: Record<string, string[]>; phase1Enabled: string[]; phase2Enabled: string[]; phase2Message: string }>("/rd/meta"),
  listFormulas: (ventureId: string, productId: string) =>
    request<RDFormula[]>(`/rd/ventures/${ventureId}/products/${productId}/formulas`),
  getFormula: (ventureId: string, formulaId: string) =>
    request<RDFormula>(`/rd/ventures/${ventureId}/formulas/${formulaId}`),
  createFormula: (ventureId: string, payload: {
    productMasterId: string; label?: string;
    forkFromFormulaId?: string;
    ingredients?: RDIngredient[];
    concentrationTarget?: string; macerationDays?: number; notes?: string;
  }) => request<RDFormula>(`/rd/ventures/${ventureId}/formulas`, {
    method: "POST", body: JSON.stringify(payload),
  }),
  patchFormula: (ventureId: string, formulaId: string, patch: Partial<RDFormula> & { ingredients?: RDIngredient[] }) =>
    request<RDFormula>(`/rd/ventures/${ventureId}/formulas/${formulaId}`, {
      method: "PATCH", body: JSON.stringify(patch),
    }),
  transitionFormula: (ventureId: string, formulaId: string, toState: string, comment?: string) =>
    request<RDFormula>(`/rd/ventures/${ventureId}/formulas/${formulaId}/transition`, {
      method: "POST", body: JSON.stringify({ toState, comment }),
    }),
  deleteFormula: (ventureId: string, formulaId: string) =>
    request<{ ok: boolean; id: string }>(`/rd/ventures/${ventureId}/formulas/${formulaId}`, {
      method: "DELETE",
    }),
  productSummary: (ventureId: string, productId: string) =>
    request<RDSummary>(`/rd/ventures/${ventureId}/products/${productId}/rd-summary`),
};

// ── Investors (Workstream 03.5) ─────────────────────────────────

export type InvestorRollup = {
  capitalCommitted: number; capitalPaid: number; capitalRemaining: number;
  distributionsPaid: number; distributionsPending: number; activeVentureCount: number;
};

export type Investor = {
  id: string; investorCode: string; fullName: string;
  email?: string | null; phone?: string | null; photoDataUrl?: string | null;
  status: string; investorClass?: string | null; joinDate?: string | null;
  bank?: { bankName?: string; accountName?: string; accountNumber?: string; iban?: string; swift?: string; currency?: string } | null;
  identity?: Array<{ kind: string; number: string; country?: string; issuedAt?: string; expiresAt?: string; documentRef?: string }>;
  kycStatus: string; kycNotes?: string | null; kycUpdatedAt?: string | null;
  amlStatus: string; amlNotes?: string | null; amlUpdatedAt?: string | null;
  notes?: string | null;
  createdAt: string; updatedAt: string;
  rollup?: InvestorRollup | { total: InvestorRollup; byVenture: any[] };
  ventureCount?: number;
};

export type Investment = {
  id: string; investorId: string; ventureId: string;
  date: string; amount: number; currency: string;
  paymentMethod: string; reference?: string | null;
  attachmentDocId?: string | null;
  status: string; approvedBy?: string | null; approvedAt?: string | null;
  notes?: string | null; createdAt: string;
};

export type OwnershipRow = {
  id: string; investorId: string; ventureId: string;
  ownershipPct: number; capitalCommitted: number;
  effectiveAt: string; reason: string; createdAt: string;
  _warning?: string;
};

export type Distribution = {
  id: string; investorId: string; ventureId: string;
  period: string; amount: number; currency: string;
  status: string; paidAt?: string | null; reference?: string | null; notes?: string | null;
  createdAt: string;
};

export type InvestorEvent = {
  id: string; investorId: string; ventureId?: string | null;
  at: string; kind: string; title: string; body?: string | null;
  actorEmail?: string | null; meta?: Record<string, any>;
};

export type CapTable = {
  ventureId: string;
  rows: Array<{ investorId: string; investorCode?: string; fullName?: string;
                 photoDataUrl?: string | null; ownershipPct: number;
                 capitalCommitted: number; effectiveAt: string; status?: string }>;
  totalOwnershipPct: number; totalCommitted: number; warning?: string | null;
};

export type InvestorMeta = {
  status: string[]; kycStatus: string[]; amlStatus: string[];
  paymentMethods: string[]; investmentStatus: string[];
  docCategories: string[]; currencies: string[];
  investorClass: string[]; investorClassDefault: string;
};

export const investors = {
  meta: () => request<InvestorMeta>("/investors/meta"),
  list: (filters: { q?: string; status?: string; ventureId?: string } = {}) => {
    const p = new URLSearchParams();
    if (filters.q) p.set("q", filters.q);
    if (filters.status) p.set("status", filters.status);
    if (filters.ventureId) p.set("ventureId", filters.ventureId);
    const qs = p.toString();
    return request<Investor[]>(`/investors/investors${qs ? "?" + qs : ""}`);
  },
  get: (id: string) => request<Investor>(`/investors/investors/${id}`),
  create: (payload: Partial<Investor> & { fullName: string }) =>
    request<Investor>(`/investors/investors`, { method: "POST", body: JSON.stringify(payload) }),
  patch: (id: string, patch: Partial<Investor>) =>
    request<Investor>(`/investors/investors/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),

  investments: (id: string) => request<Investment[]>(`/investors/investors/${id}/investments`),
  createInvestment: (id: string, payload: Omit<Investment, "id" | "investorId" | "status" | "createdAt">) =>
    request<Investment>(`/investors/investors/${id}/investments`, { method: "POST", body: JSON.stringify(payload) }),
  approveInvestment: (investmentId: string) =>
    request<Investment>(`/investors/investments/${investmentId}/approve`, { method: "POST" }),

  ownership: (id: string) => request<OwnershipRow[]>(`/investors/investors/${id}/ownership`),
  setOwnership: (id: string, payload: Omit<OwnershipRow, "id" | "investorId" | "createdAt">) =>
    request<OwnershipRow>(`/investors/investors/${id}/ownership`, { method: "POST", body: JSON.stringify(payload) }),
  capTable: (ventureId: string) => request<CapTable>(`/investors/ventures/${ventureId}/cap-table`),

  distributions: (id: string) => request<Distribution[]>(`/investors/investors/${id}/distributions`),
  createDistribution: (id: string, payload: Omit<Distribution, "id" | "investorId" | "createdAt">) =>
    request<Distribution>(`/investors/investors/${id}/distributions`, { method: "POST", body: JSON.stringify(payload) }),

  timeline: (id: string) => request<InvestorEvent[]>(`/investors/investors/${id}/timeline`),

  documents: (id: string) =>
    request<Array<{ id: string; investorId: string; ventureId?: string; category: string; name: string; sizeBytes: number; uploadedAt: string; notes?: string }>>(
      `/investors/investors/${id}/documents`),

  exportUrl: (id: string, kind: "profile" | "statement" | "certificate", ventureId?: string) => {
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/pureh/investors/investors/${id}/export/${kind}`;
    return ventureId ? `${base}?ventureId=${encodeURIComponent(ventureId)}` : base;
  },
};

/**
 * Convenience wrapper for read requests that transparently refreshes the
 * access token on 401 and retries the request once.
 */
export async function authedGet<T>(path: string): Promise<T> {
  try {
    return await request<T>(path);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      await api.refresh();
      return await request<T>(path);
    }
    throw e;
  }
}


// ==============================================================
// Workstream 04 · Inventory · Slice 1
// ==============================================================
export type InvMoney = {
  originalAmount: number; originalCurrency: string;
  baseAmount: number; baseCurrency: string;
  rateUsed: number; convertedAt: string; rateSource?: string;
};
export type InvWarehouse = {
  id: string; ventureId: string; docNumber: string;
  type: "raw_materials"|"packaging"|"wip"|"finished_goods"|"quarantine"|"showroom";
  name: string; costCenterId?: string|null; responsibleUserId?: string|null;
  address?: string|null; notes?: string|null; active: boolean;
  createdAt: string; updatedAt: string; createdBy?: string|null;
};
export type InvBin = {
  id: string; ventureId: string; warehouseId: string; docNumber: string;
  code: string; capacity?: number|null; accessNotes?: string|null;
  createdAt: string; updatedAt: string;
};
export type InvStockItem = {
  id: string; ventureId: string; kind: string; itemRefId: string;
  lotLabel?: string|null; warehouseId: string; binId?: string|null;
  quantity: number; unit: string; unitCost: InvMoney;
  receivedAt: string; expiryAt?: string|null; sourceGrnId?: string|null;
  notes?: string|null; createdAt: string; updatedAt: string;
};
export type InvMovement = {
  id: string; ventureId: string; docNumber: string;
  type: "receive"|"adjust"; status: "posted"|"reversed"; postedAt: string; postedBy?: string;
  toWarehouseId?: string|null; toBinId?: string|null;
  fromWarehouseId?: string|null; fromBinId?: string|null;
  stockItemId: string; kind: string; itemRefId: string;
  quantity: number; unit: string;
  unitCost: InvMoney; totalCost: InvMoney;
  reason: string; reasonNote?: string|null;
  reference?: { type: string; id: string; docNumber?: string };
  costCenterId?: string|null;
};
export type InvGRNLine = {
  kind: string; itemRefId: string; lotLabel: string;
  quantity: number; unit: string;
  unitCostAmount: number; unitCostCurrency: string;
  binId?: string|null; expiryAt?: string|null; notes?: string|null;
};
export type InvGRN = {
  id: string; ventureId: string; docNumber: string;
  warehouseId: string; supplierName: string; supplierRef?: string|null;
  invoiceDate?: string|null; notes?: string|null;
  lines: InvGRNLine[]; status: "draft"|"posted"|"cancelled";
  postedAt?: string|null; postedBy?: string|null;
  lineCount: number; totalUnits: number;
  createdAt: string; updatedAt: string; createdBy?: string|null;
};
export type InvAdjustment = {
  id: string; ventureId: string; docNumber: string;
  stockItemId: string; warehouseId: string; binId?: string|null;
  delta: number; unit?: string|null; reason: string; note?: string|null;
  costCenterId?: string|null; status: string; postedAt: string; postedBy?: string;
};
export type InvOnHandReport = {
  generatedAt: string; baseCurrency: string|null;
  totalValueBase: number; lotCount: number;
  stockItems: Array<InvStockItem & { warehouse?: InvWarehouse; itemRef?: { id: string; code?: string; docNumber?: string; name?: string; unit?: string } }>;
  byWarehouse: Array<{ warehouseId: string; warehouse?: InvWarehouse; lotCount: number; totalUnits: number; totalValueBase: number }>;
};
export type InvMeta = {
  warehouseTypes: string[]; adjustmentReasons: string[];
  movementTypes: string[]; movementStatuses: string[];
  stockItemKinds: string[]; units: string[]; slice: number;
};

export const inventory = {
  meta: () => request<InvMeta>("/inventory/meta"),
  listWarehouses: (ventureId: string, opts: { includeInactive?: boolean; type?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.includeInactive) p.set("includeInactive", "true");
    if (opts.type) p.set("type", opts.type);
    const qs = p.toString();
    return request<InvWarehouse[]>(`/inventory/ventures/${ventureId}/warehouses${qs ? "?" + qs : ""}`);
  },
  createWarehouse: (ventureId: string, payload: Partial<InvWarehouse> & { type: string; name: string }) =>
    request<InvWarehouse>(`/inventory/ventures/${ventureId}/warehouses`, {
      method: "POST", body: JSON.stringify(payload),
    }),
  getWarehouse: (ventureId: string, whId: string) =>
    request<InvWarehouse>(`/inventory/ventures/${ventureId}/warehouses/${whId}`),
  patchWarehouse: (ventureId: string, whId: string, patch: Partial<InvWarehouse>) =>
    request<InvWarehouse>(`/inventory/ventures/${ventureId}/warehouses/${whId}`, {
      method: "PATCH", body: JSON.stringify(patch),
    }),
  deactivateWarehouse: (ventureId: string, whId: string) =>
    request<InvWarehouse>(`/inventory/ventures/${ventureId}/warehouses/${whId}/deactivate`, { method: "POST" }),

  listBins: (ventureId: string, whId: string) =>
    request<InvBin[]>(`/inventory/ventures/${ventureId}/warehouses/${whId}/bins`),
  createBin: (ventureId: string, whId: string, payload: { code: string; capacity?: number; accessNotes?: string }) =>
    request<InvBin>(`/inventory/ventures/${ventureId}/warehouses/${whId}/bins`, {
      method: "POST", body: JSON.stringify(payload),
    }),
  patchBin: (ventureId: string, binId: string, patch: Partial<InvBin>) =>
    request<InvBin>(`/inventory/ventures/${ventureId}/bins/${binId}`, {
      method: "PATCH", body: JSON.stringify(patch),
    }),
  deleteBin: (ventureId: string, binId: string) =>
    request<{ ok: boolean; id: string }>(`/inventory/ventures/${ventureId}/bins/${binId}`, { method: "DELETE" }),

  listStock: (ventureId: string, opts: { warehouseId?: string; binId?: string; itemRefId?: string; kind?: string; includeEmpty?: boolean } = {}) => {
    const p = new URLSearchParams();
    if (opts.warehouseId) p.set("warehouseId", opts.warehouseId);
    if (opts.binId) p.set("binId", opts.binId);
    if (opts.itemRefId) p.set("itemRefId", opts.itemRefId);
    if (opts.kind) p.set("kind", opts.kind);
    if (opts.includeEmpty) p.set("includeEmpty", "true");
    const qs = p.toString();
    return request<InvStockItem[]>(`/inventory/ventures/${ventureId}/stock${qs ? "?" + qs : ""}`);
  },
  getStock: (ventureId: string, itemId: string) =>
    request<{ stockItem: InvStockItem; movements: InvMovement[] }>(`/inventory/ventures/${ventureId}/stock/${itemId}`),

  listMovements: (ventureId: string, opts: { warehouseId?: string; stockItemId?: string; type?: string; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (opts.warehouseId) p.set("warehouseId", opts.warehouseId);
    if (opts.stockItemId) p.set("stockItemId", opts.stockItemId);
    if (opts.type) p.set("type", opts.type);
    if (opts.limit) p.set("limit", String(opts.limit));
    const qs = p.toString();
    return request<InvMovement[]>(`/inventory/ventures/${ventureId}/movements${qs ? "?" + qs : ""}`);
  },

  listGRN: (ventureId: string, status?: string) => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    const qs = p.toString();
    return request<InvGRN[]>(`/inventory/ventures/${ventureId}/grn${qs ? "?" + qs : ""}`);
  },
  createGRN: (ventureId: string, payload: {
    warehouseId: string; supplierName: string; supplierRef?: string;
    invoiceDate?: string; notes?: string; lines: InvGRNLine[];
  }) =>
    request<InvGRN>(`/inventory/ventures/${ventureId}/grn`, {
      method: "POST", body: JSON.stringify(payload),
    }),
  getGRN: (ventureId: string, grnId: string) =>
    request<InvGRN>(`/inventory/ventures/${ventureId}/grn/${grnId}`),
  patchGRN: (ventureId: string, grnId: string, patch: Partial<Omit<InvGRN, "id"|"ventureId"|"status">>) =>
    request<InvGRN>(`/inventory/ventures/${ventureId}/grn/${grnId}`, {
      method: "PATCH", body: JSON.stringify(patch),
    }),
  postGRN: (ventureId: string, grnId: string) =>
    request<{ grn: InvGRN; movements: InvMovement[]; stockItems: InvStockItem[] }>(
      `/inventory/ventures/${ventureId}/grn/${grnId}/post`, { method: "POST" },
    ),
  cancelGRN: (ventureId: string, grnId: string) =>
    request<InvGRN>(`/inventory/ventures/${ventureId}/grn/${grnId}/cancel`, { method: "POST" }),

  listAdjustments: (ventureId: string, limit = 200) =>
    request<InvAdjustment[]>(`/inventory/ventures/${ventureId}/adjustments?limit=${limit}`),
  postAdjustment: (ventureId: string, payload: {
    stockItemId: string; delta: number; reason: string;
    costCenterId?: string; note?: string;
  }) =>
    request<{ adjustment: InvAdjustment; movement: InvMovement; newQuantity: number }>(
      `/inventory/ventures/${ventureId}/adjustments`,
      { method: "POST", body: JSON.stringify(payload) },
    ),

  onHandReport: (ventureId: string, opts: { warehouseId?: string; kind?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.warehouseId) p.set("warehouseId", opts.warehouseId);
    if (opts.kind) p.set("kind", opts.kind);
    const qs = p.toString();
    return request<InvOnHandReport>(`/inventory/ventures/${ventureId}/report/on-hand${qs ? "?" + qs : ""}`);
  },
};
